import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { BygrModule, ExternalLink, SingleFilterDropDown, ToggleTooltip, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, ButtonBar, StickyContainer, Tabs } from '@ygtechnologies/rubus-ui';
import React, { ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { useAsyncState, useQueryState } from '@/utils/hooks';

import { SimplePageTitle } from '@/components/layout/PageTitle';
import { useHistory } from 'react-router';
import { generatePreview, getAllInternalMeetingsTypes, getAllLetterSetupData, MeetingType } from '@/services/booking';
import { FilterItem } from '@/types';
import { MeetingApartments } from './MeetingLetter/MeetingApartments';
import { MeetingLetterSetup } from './MeetingLetter/MeetingLetterSetup';
import { LetterChecklistId } from './MeetingLetter/MeetingLetterSetup/MeetingLetterSetup';
import { MeetingLetterText } from './MeetingLetter/MeetingLetterText';
import theme from './MeetingOverview.module.scss';
import MeetingsFilter, { MeetingFilterOptions } from './MeetingsFilter';
import { OnChangeValue } from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
// import { MeetingHistoryLog } from './MeetingLetter/MeetingHistoryLog/MeetingHistoryLog';

const MeetingLetterPreview = React.lazy(() => import('./MeetingLetter/MeetingLetterPreview/MeetingLetterPreview'));

interface MeetingOverviewProps {
    projectId?: number;
}

export enum SetupTabId {
    Overview = 'overview',
    Setup = 'setup',
    Text = 'text',
    Preview = 'preview',
    // HistoryLog = 'historylog',
}
interface SetupTabDefinition {
    id: SetupTabId;
    caption(): ReactNode;
    render(props: {
        activeMeetingType: MeetingType;
        allMeetingsTypes: MeetingType[];
        letterSetupData?: LetterSetupData;
        pdfFile: FileData | null;
        projectId: number;
        filter: MeetingFilterOptions;
        reload(): void;
    }): ReactNode;
}
export interface LetterSetupData {
    id?: number;
    projectId?: number;
    cardImageToken?: string | undefined;
    fileId?: number | undefined;
    logoImageToken?: string | undefined;
    supportLogoImageToken?: string | undefined;
    connectedMeetings?: number[];
    meetingType?: MeetingType;
    contactName?: string;
    contactEmail?: string;
    contactFreeText?: string;
    headerText?: string;
    contentText?: string;
    senderFreeText?: string;
    attachmentText?: string;
    buttonText?: string;
    cardHeader?: string;
    cardText?: string;
    optionalLetterSetupFields?: LetterChecklistId[];
    sendEmail?: boolean;
    sendSms?: boolean;
    showSenderInfo?: boolean;
    showCardImage?: boolean;
}

interface MeetingTypeTabDefinition {
    meetingType: MeetingType;
    caption(): ReactNode;
}

const setupTabDefinitions: SetupTabDefinition[] = [
    {
        id: SetupTabId.Overview,
        caption: () => <Trans>Overview</Trans>,
        render: ({ activeMeetingType, letterSetupData, filter, reload }) => (
            <MeetingApartments
                letterSetup={letterSetupData}
                activeMeetingType={activeMeetingType}
                filter={filter}
                onSaved={reload}
            />
        ),
    },
    {
        id: SetupTabId.Setup,
        caption: () => <Trans>Setup</Trans>,
        render: ({ letterSetupData, allMeetingsTypes, reload }) => (
            <MeetingLetterSetup letterSetup={letterSetupData} allMeetingsTypes={allMeetingsTypes} onSaved={reload} />
        ),
    },
    {
        id: SetupTabId.Text,
        caption: () => <Trans>Letter</Trans>,
        render: ({ letterSetupData, allMeetingsTypes, reload }) => (
            <MeetingLetterText letterSetup={letterSetupData} allMeetingsTypes={allMeetingsTypes} onSaved={reload} />
        ),
    },
    {
        id: SetupTabId.Preview,
        caption: () => <Trans>Preview</Trans>,
        render: ({ letterSetupData, pdfFile }) => (
            <Suspense fallback={<LoadingIndicator />}>
                <MeetingLetterPreview letterSetup={letterSetupData} pdfFile={pdfFile} />
            </Suspense>
        ),
    },
    // {
    //     id: SetupTabId.HistoryLog,
    //     caption: () => <Trans>History log</Trans>,
    //     render: ({ letterSetupData }) => <MeetingHistoryLog letterSetup={letterSetupData} />,
    // },
];

const MeetingOverview: React.FC<MeetingOverviewProps> = () => {
    const project = useSelector((state: IState) => state.global.activeProject!);
    const projectId = project.id;
    const [isLoading, setIsLoading] = useState(true);
    const [activeMeetingTypeId, setActiveMeetingTypeId] = useState<number | undefined>(undefined);

    // const [meetingTypeTabDefinitions, setMeetingTypeTabDefinitions] = useState<MeetingTypeTabDefinition[]>([]);

    const [selectedMeetingTypeTabDefinition, setSelectedMeetingTypeTabDefinition] = useState<MeetingTypeTabDefinition>();
    const [selectedSetupTabDefinition, setSelectedSetupTabDefinition] = useState(setupTabDefinitions[0]);
    const [selectedSetupData, setSelectedSetupData] = useState<LetterSetupData>();
    const [filter, setFilter] = useQueryState<MeetingFilterOptions>(MeetingFilterOptions.empty());

    const setActiveMeetingTypeTab = (tab: MeetingTypeTabDefinition) => {
        const newSetupData = allSetups?.find((x) => x.meetingType?.id === tab!.meetingType.id);
        setActiveMeetingTypeId(tab.meetingType.id);
        setSelectedSetupData(newSetupData);
        setSelectedMeetingTypeTabDefinition(tab);
        localStorage.setItem(`${project.id}:activeMeeting`, '' + tab.meetingType.id!);
    };

    const setActiveSetupTab = (tab: SetupTabDefinition) => {
        setSelectedSetupTabDefinition(tab);
    };
    const handleReload = () => {
        setIsLoading(true);
        reloadAllSetups();
    };

    const filterChange = (value: OnChangeValue<FilterItem, false>) => {
        if (value) {
            const tab = meetingTypeTabDefinitions!.find((x) => x.meetingType.id === value.id);
            setActiveMeetingTypeTab(tab!);
        }
    };

    const [allMeetingsTypes] = useAsyncState<MeetingType[] | null>(
        () => getAllInternalMeetingsTypes(projectId),
        [projectId],
        null
    );

    const [allSetups, , reloadAllSetups] = useAsyncState<LetterSetupData[] | null>(
        () => getAllLetterSetupData(projectId),
        [projectId],
        null
    );

    const [pdfFile, setPdfFile] = useAsyncState<FileData | null>(
        () => {
            if (activeMeetingTypeId) {
                setPdfFile(null);
                return generatePreview(activeMeetingTypeId);
            } else {
                return Promise.resolve(null);
            }
        },
        [activeMeetingTypeId, allSetups],
        null
    );

    const meetingTypeTabDefinitions = useMemo<MeetingTypeTabDefinition[] | null>(() => {
        if (!allMeetingsTypes || !allSetups) {
            return null;
        }

        return allMeetingsTypes.map((x) => {
            return {
                meetingType: x,
                caption: () => <>{x.name}</>,
            };
        });
    }, [allMeetingsTypes, allSetups]);

    const history = useHistory();

    const getRedirectUrl = (module: BygrModule) => {
        const baseUrl =
            module === BygrModule.ChangeManagement
                ? window.APP_CONFIG.CHANGE_MANAGEMENT_URL
                : window.APP_CONFIG.AFTERMARKET_URL;
        return `${baseUrl}/projects/${projectId}/settings/features`;
    };

    useEffect(() => {
        if (allSetups && meetingTypeTabDefinitions) {
            if (meetingTypeTabDefinitions && meetingTypeTabDefinitions.length === 0) {
                history.push('meetingtypes');
                return;
            }
            const storedId = localStorage.getItem(`${project.id}:activeMeeting`);
            if (storedId !== null && storedId !== undefined) {
                const tab = meetingTypeTabDefinitions!.find((x) => x.meetingType.id === +storedId);
                setActiveMeetingTypeTab(tab!);
            } else {
                setActiveMeetingTypeTab(meetingTypeTabDefinitions[0]);
            }
        }
    }, [allSetups, meetingTypeTabDefinitions]);

    if (!allMeetingsTypes || !allSetups) {
        return <LoadingIndicator />;
    }

    if (!selectedMeetingTypeTabDefinition) {
        return (
            <div className={theme.noMeetings}>
                <Trans>You have no meeting types. Please create at least one to continue</Trans>
            </div>
        );
    }

    return (
        <div>
            <SimplePageTitle title={t`Meetings`} />
            <ButtonBar noWrap margins="none" className={theme.actionBar} sticky>
                <div className={theme.meetingTypes}>
                    <SingleFilterDropDown
                        onChange={(v) => filterChange(v)}
                        placeholder={t`Select meeting type`}
                        data={meetingTypeTabDefinitions!.map((x) => {
                            return { id: x.meetingType.id, name: x.caption() };
                        })}
                        value={{
                            id: activeMeetingTypeId,
                            name: selectedMeetingTypeTabDefinition.caption(),
                        }}
                        label={''}
                    />
                </div>

                <Tabs
                    activeId={selectedSetupTabDefinition.id}
                    onChange={(id) => {
                        const tab = setupTabDefinitions.find((x) => x.id === id);
                        if (tab) setActiveSetupTab(tab);
                    }}
                    variant="pill"
                    size="sm">
                    {setupTabDefinitions.map((x) => (
                        <Tabs.Tab key={x.id} id={x.id} label={x.caption()} />
                    ))}
                </Tabs>
                <ToggleTooltip
                    content={
                        <Trans>
                            Remember to enable meetings in the relevant modules and buildings:{' '}
                            <ExternalLink
                                className={theme.infoLink}
                                href={getRedirectUrl(BygrModule.ChangeManagement)}>
                                Change management
                            </ExternalLink>{' '}
                            and{' '}
                            <ExternalLink className={theme.infoLink} href={getRedirectUrl(BygrModule.Aftermarket)}>
                                Aftermarket
                            </ExternalLink>
                        </Trans>
                    }
                    placement="bottom"
                    dynamicWidth>
                    <button type="button" className={theme.infoIconButton} aria-label="Meeting information">
                        <FontAwesomeIcon icon={faExclamationCircle} />
                    </button>
                </ToggleTooltip>
                {selectedSetupTabDefinition.id === SetupTabId.Overview && (
                    <ButtonBar.Right>
                        <MeetingsFilter filter={filter} onFilterChange={setFilter} />
                    </ButtonBar.Right>
                )}
            </ButtonBar>
            <AlertBanner type="warning" className={theme.infoBanner} stretch>
                <Trans>
                    Remember to enable meetings in the relevant modules and buildings:{' '}
                    <ExternalLink href={getRedirectUrl(BygrModule.ChangeManagement)}>
                        Change management
                    </ExternalLink>{' '}
                    and{' '}
                    <ExternalLink href={getRedirectUrl(BygrModule.Aftermarket)}>
                        Aftermarket
                    </ExternalLink>
                </Trans>
            </AlertBanner>
            <StickyContainer>
                <div className={theme.tabContent}>
                    {selectedSetupTabDefinition.render({
                        projectId,
                        letterSetupData: selectedSetupData,
                        pdfFile: pdfFile,
                        allMeetingsTypes: allMeetingsTypes!,
                        activeMeetingType: selectedMeetingTypeTabDefinition!.meetingType,
                        reload: handleReload,
                        filter,
                    })}
                </div>
            </StickyContainer>
        </div>
    );
};

export default MeetingOverview;
